/*
 * Copyright 2026 Sony Corporation
 */

import type { CreativeLook } from '@ips-device-verse/twin-service/schema';
import { useCallback, useEffect, useRef, useState } from 'react';

import { createCameraPreview, type CameraPreview } from '../scene/camera-preview';
import { DEFAULT_OPTICS, type ResolvedOptics } from '../scene/camera-optics';
import type { RigManager } from '../scene/rig-manager';
import type { SceneEditor } from '../scene/scene-editor';

export interface PreviewContext {
  editor: SceneEditor;
  rigs?: RigManager;
}

export interface UseCameraPreviewOptions {
  deviceId: string | undefined;
  look: Required<CreativeLook>;
  optics?: ResolvedOptics;
  depthOfField?: boolean;
}

export interface UseCameraPreviewResult {
  /** Called from the canvas's onReady, once the editor exists. */
  attach: (context: PreviewContext) => void;
  /** Handed to the preview canvas element. */
  canvasRef: (canvas: HTMLCanvasElement | null) => void;
}

/**
 * Binds the preview to whichever camera is selected.
 *
 * The editor and the canvas arrive from opposite ends of the tree and in either
 * order, so the preview is built by an effect that waits for both.
 */
export const useCameraPreview = ({
  deviceId,
  look,
  optics = DEFAULT_OPTICS,
  depthOfField = false,
}: UseCameraPreviewOptions): UseCameraPreviewResult => {
  const [context, setContext] = useState<PreviewContext>();
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const previewRef = useRef<CameraPreview>(null);

  useEffect(() => {
    if (!context || !canvas) {
      return;
    }

    const preview = createCameraPreview({ editor: context.editor, canvas });
    previewRef.current = preview;

    return () => {
      previewRef.current = null;
      preview.dispose();
    };
  }, [context, canvas]);

  // Deliberately every render: a rig is created some renders after the camera it
  // belongs to is selected, and both calls are no-ops when nothing changed.
  useEffect(() => {
    previewRef.current?.follow(context?.rigs?.rootOf(deviceId));
    previewRef.current?.setLook(look);
    previewRef.current?.setOptics(optics);
    previewRef.current?.setDepthOfField(depthOfField);
  });

  return { attach: useCallback((next: PreviewContext) => setContext(next), []), canvasRef: setCanvas };
};
